import React, { useEffect, useState } from "react";
import api, { apiErrorMsg, resolveImage } from "../lib/api";
import { emitDataChange, useDataSync, useAutoRefresh } from "../lib/dataSync";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { BadgeCheck, Clock, XCircle, User, Crown, Lock, Phone, MapPin } from "lucide-react";

const STATUS_CHIP = {
  approved: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]",
  pending: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  rejected: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
};

export default function AdminDealersTab() {
  const [dealers, setDealers] = useState([]);
  const [plans, setPlans] = useState([]);
  const load = async () => {
    const [d, p] = await Promise.all([
      api.get("/admin/dealers"),
      api.get("/dealer/plans"),
    ]);
    setDealers(d.data);
    setPlans(p.data || []);
  };
  useEffect(() => { load(); }, []);

  // v2.72 — Live sync (KYC submissions, user/plan changes) + 45 s poll.
  useDataSync(["dealers", "users"], load);
  useAutoRefresh(load, 45000);

  const approve = async (uid) => {
    try {
      await api.post(`/admin/dealers/${uid}/verify`);
      toast.success("Dealer verified");
      emitDataChange(["dealers", "users", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };
  const reject = async (uid) => {
    try {
      await api.post(`/admin/dealers/${uid}/reject-kyc`);
      toast.success("KYC rejected");
      emitDataChange(["dealers", "users", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const grantPlan = async (uid, planId) => {
    try {
      await api.post(`/admin/dealers/${uid}/plan`, { plan_id: planId });
      toast.success("Plan activated");
      emitDataChange(["dealers", "users", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  const revokePlan = async (uid) => {
    if (!window.confirm("Revoke this dealer's plan?")) return;
    try {
      await api.delete(`/admin/dealers/${uid}/plan`);
      toast.success("Plan revoked");
      emitDataChange(["dealers", "users", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err)); }
  };

  // v2.70 Phase 3 — Territory editor: save comma-separated areas for lead routing
  const saveTerritories = async (uid, raw) => {
    const list = (raw || "").split(",").map((s) => s.trim()).filter(Boolean);
    try {
      await api.patch(`/admin/users/${uid}`, { territories: list });
      toast.success(`Territories updated (${list.length})`);
      emitDataChange(["dealers", "users", "dashboard"]);
    } catch (err) { toast.error(apiErrorMsg(err, "Couldn't save territories")); }
  };

  if (!dealers.length) {
    return (
      <div className="text-center py-10 text-[#A1A1AA] bg-white border border-dashed border-[#E4E4E7] rounded-sm" data-testid="dealers-empty">
        No dealers registered yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {dealers.map((d) => {
        const kyc = d.kyc;
        const status = kyc?.status || "no-kyc";
        return (
          <div key={d.id} className="bg-white border border-[#E4E4E7] rounded-sm p-4" data-testid={`dealer-${d.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-heading font-semibold text-[15px] tracking-tight inline-flex items-center gap-1.5">
                    <User size={14} /> {d.name}
                    {d.verified_partner && <BadgeCheck size={14} className="text-[#15803D]" />}
                  </h4>
                  {kyc && (
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm ${STATUS_CHIP[status] || "bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]"}`}>
                      KYC {status}
                    </span>
                  )}
                  {!kyc && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]">
                      No KYC
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-[#52525B] mt-1" data-testid={`dealer-email-${d.id}`}>{d.email}</div>
                <div className="text-[12px] text-[#52525B] mt-0.5 font-mono-data inline-flex items-center gap-1" data-testid={`dealer-phone-${d.id}`}>
                  <Phone size={11} className="text-[#71717A]" />
                  {d.phone ? (
                    <a href={`tel:${d.phone}`} className="hover:text-[#0055FF]">{d.phone}</a>
                  ) : (
                    <span className="text-[#A1A1AA]">No phone on file</span>
                  )}
                </div>
                <div className="text-[11px] text-[#A1A1AA] mt-1 flex gap-4 flex-wrap">
                  <span>Listings: <b className="text-[#09090B] font-mono-data">{d.listings_count}</b> ({d.approved_count} approved)</span>
                  <span>Free left: <b className={d.free_listings_remaining === 0 ? "text-[#DC2626]" : "text-[#15803D]"}>{d.free_listings_remaining}</b></span>
                </div>
                {kyc && (
                  <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm">
                    <div className="text-[11px] uppercase tracking-wider text-[#52525B] font-semibold">KYC Document</div>
                    <div className="text-[12px] mt-1"><b>Name on ID:</b> {kyc.full_name || "—"} · <b>Type:</b> {kyc.id_type}</div>
                    {kyc.id_image_url && (
                      <a href={resolveImage(kyc.id_image_url)} target="_blank" rel="noreferrer" className="text-[11px] text-[#0055FF] hover:underline" data-testid={`kyc-view-${d.id}`}>
                        View ID Image →
                      </a>
                    )}
                  </div>
                )}
                <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm" data-testid={`dealer-territories-row-${d.id}`}>
                  <div className="text-[11px] uppercase tracking-wider text-[#52525B] font-semibold inline-flex items-center gap-1.5 mb-1.5">
                    <MapPin size={11} /> Operating Territories
                  </div>
                  <div className="flex gap-2 items-start">
                    <input
                      type="text"
                      defaultValue={(d.territories || []).join(", ")}
                      placeholder="e.g. Dwarka, Uttam Nagar, Nawada"
                      data-testid={`dealer-territories-input-${d.id}`}
                      className="flex-1 h-8 px-2 text-[12.5px] border border-[#E4E4E7] rounded-sm bg-white focus:outline-none focus:border-[#0055FF]"
                      onKeyDown={(e) => { if (e.key === "Enter") saveTerritories(d.id, e.target.value); }}
                      onBlur={(e) => {
                        const next = e.target.value.trim();
                        const cur = (d.territories || []).join(", ");
                        if (next !== cur) saveTerritories(d.id, next);
                      }}
                    />
                  </div>
                  <p className="text-[10px] text-[#A1A1AA] mt-1">
                    Comma-separated main_areas. Used to auto-route matching client leads to this dealer.
                  </p>
                </div>

                <div className="mt-3 p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm" data-testid={`dealer-plan-row-${d.id}`}>
                  <div className="text-[11px] uppercase tracking-wider text-[#52525B] font-semibold inline-flex items-center gap-1.5">
                    <Crown size={11} /> Dealer Plan
                  </div>
                  {d.dealer_plan?.active ? (
                    <div className="mt-1.5 flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-[12px]">
                        <span className="bg-[#D1FAE5] text-[#065F46] text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm border border-[#A7F3D0]">{d.dealer_plan.name || d.dealer_plan.plan_id} · ACTIVE</span>
                        {d.dealer_plan.expires_at && (
                          <span className="ml-2 text-[#71717A]">until {new Date(d.dealer_plan.expires_at).toLocaleDateString()}</span>
                        )}
                      </div>
                      <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={() => revokePlan(d.id)} data-testid={`dealer-plan-revoke-${d.id}`}>
                        Revoke
                      </Button>
                    </div>
                  ) : (
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-[#71717A] inline-flex items-center gap-1"><Lock size={10} /> No active plan</span>
                      {plans.map((p) => (
                        <Button
                          key={p.id}
                          size="sm"
                          variant="outline"
                          className="rounded-sm h-7 px-2"
                          onClick={() => grantPlan(d.id, p.id)}
                          data-testid={`dealer-plan-grant-${d.id}-${p.id}`}
                        >
                          Grant {p.name} (₹{p.price_inr})
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {kyc?.status === "pending" && (
                <div className="flex gap-2">
                  <Button size="sm" className="btn-brand rounded-sm" onClick={() => approve(d.id)} data-testid={`approve-kyc-${d.id}`}>
                    <BadgeCheck size={13} className="mr-1.5" /> Approve KYC
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-sm" onClick={() => reject(d.id)} data-testid={`reject-kyc-${d.id}`}>
                    <XCircle size={13} className="mr-1.5" /> Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
